'use client';

import { useEyeTracking } from './useEyeTracking';

interface RainaHeadProps {
  /** Unique id used to namespace this instance's SVG defs */
  svgId: string;
  /** Enable pupils following the cursor */
  eyeTracking: boolean;
  /** Whether a blink is occurring (driven by useRainaAnimation) */
  isBlinking?: boolean;
  /** Enable idle behaviors (eye wander) */
  idleAnimations?: boolean;
  /** Called when the blink animation finishes so the animation state can reset */
  onBlinkEnd?: () => void;
}

// Eye geometry in the 26x28 viewBox. Pupils sit slightly nasal within the whites; the
// travel room (white r - pupil r ~= 0.58u) bounds how far the pupils can move.
const RIGHT_EYE_WHITE =
  'M17.4911 15.4067c1.2901 0 2.3359 1.0355 2.3359 2.3128s-1.0458 2.3128-2.3359 2.3128-2.3359-1.0355-2.3359-2.3128 1.0458-2.3128 2.3359-2.3128';
const RIGHT_PUPIL =
  'M16.9876 15.6695c.958 0 1.7346.7766 1.7346 1.7346s-.7766 1.7346-1.7346 1.7346-1.7345-.7766-1.7345-1.7346.7765-1.7346 1.7345-1.7346';
const LEFT_EYE_WHITE =
  'M8.67775 15.3851c1.29008 0 2.33585 1.0354 2.33585 2.3127 0 1.2774-1.04577 2.3128-2.33585 2.3128s-2.33589-1.0354-2.33589-2.3128c0-1.2773 1.04581-2.3127 2.33589-2.3127';
const LEFT_PUPIL =
  'M8.18263 15.6001c.95798 0 1.73458.7766 1.73458 1.7346s-.7766 1.7346-1.73458 1.7346-1.73457-.7766-1.73457-1.7346.77659-1.7346 1.73457-1.7346';

/**
 * The current MagicSchool Raina mascot head — a compact, single-SVG character head with
 * cursor eye-tracking and blinking. Unlike the legacy multi-part head it has no expressions,
 * head tilt, ear twitch, nod, or wave.
 *
 * Rendered by `Raina` for `variant="head"`; not exported from the package directly.
 */
export function RainaHead({ svgId, eyeTracking, isBlinking = false, idleAnimations = true, onBlinkEnd }: RainaHeadProps) {
  const id = (name: string) => `${svgId}${name}`;

  const { containerRef, eyeOffset, isIdle } = useEyeTracking({
    enabled: eyeTracking,
    enableWander: idleAnimations,
    containerWidth: 26,
    containerHeight: 28,
    faceCenterX: 12.585, // midpoint of the two pupils
    faceCenterY: 17.365,
    // ~0.58u of room between pupil (r≈1.73) and white (r≈2.31) edges in every direction.
    maxOffsetOutward: 0.55,
    maxOffsetUp: 0.5,
    maxOffsetDown: 0.55,
  });

  // The artwork rests each pupil up-and-left of its white; recenter it (base offsets = white
  // center − pupil center, per eye) so the neutral gaze is centered and the cursor pulls the
  // pupils symmetrically. When fully static (no eye tracking and no idle animation) hold a fixed
  // pose looking up and to her left (the viewer's right) instead of staring straight ahead.
  const isStatic = !eyeTracking && !idleAnimations;
  const gazeX = isStatic ? 0.38 : 0; // +x = her left (viewer's right)
  const gazeY = isStatic ? -0.3 : 0; // -y = up
  const pupilTransition = isIdle ? 'transform 150ms ease-out' : 'none';
  // baseY recenters each pupil vertically within its white (whites differ slightly in y).
  const pupilStyle = (baseY: number) =>
    ({
      transform: `translate(${0.5 + gazeX + eyeOffset.x}px, ${baseY + gazeY + eyeOffset.y}px)`,
      transition: pupilTransition,
    }) as const;

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {/* Inset the glyph within its layout box so it isn't edge-to-edge (~26px wide at the xs/36px
          size, scaling proportionally for larger sizes). containerRef sits on this inner box so
          eye-tracking maps the cursor against the actual rendered face. */}
      <div ref={containerRef} className="relative" style={{ width: '72%', aspectRatio: '26 / 28' }}>
        <svg aria-hidden="true" className="block size-full" fill="none" viewBox="0 0 26 28">
          <g clipPath={`url(#${id('a')})`}>
            {/* Face */}
            <g filter={`url(#${id('b')})`}>
              <path
                fill={`url(#${id('c')})`}
                d="M23.9347 15.3454c.3546 5.2731-4.8569 9.2048-10.9089 9.2048-6.96112 0-11.43246-4.7335-10.8774-9.4515.5328-4.5289 4.90307-8.17182 10.8774-8.17182 6.3604 0 10.4617 3.73122 10.9089 8.41852"
              />
            </g>

            {/* Eyes — wrapped in one group that scales vertically on blink */}
            <g
              className={isBlinking ? 'motion-safe:animate-[raina-head-blink_0.25s_ease-in-out]' : ''}
              style={{ transformOrigin: '12.5px 17.6px', willChange: 'transform' }}
              onAnimationEnd={(e) => {
                e.stopPropagation();
                onBlinkEnd?.();
              }}
            >
              {/* Right eye */}
              <path fill={`url(#${id('d')})`} d={RIGHT_EYE_WHITE} />
              <path fill="#fff" d={RIGHT_EYE_WHITE} />
              <g clipPath={`url(#${id('rightWhiteClip')})`}>
                <g style={pupilStyle(0.32)}>
                  <path fill={`url(#${id('e')})`} d={RIGHT_PUPIL} />
                  <path fill="#33116f" d={RIGHT_PUPIL} />
                </g>
              </g>

              {/* Left eye */}
              <path fill={`url(#${id('f')})`} d={LEFT_EYE_WHITE} />
              <path fill="#fff" d={LEFT_EYE_WHITE} />
              <g clipPath={`url(#${id('leftWhiteClip')})`}>
                <g style={pupilStyle(0.36)}>
                  <path fill={`url(#${id('g')})`} d={LEFT_PUPIL} />
                  <path fill="#33116f" d={LEFT_PUPIL} />
                </g>
              </g>
            </g>

            {/* Mouth */}
            <path stroke="#33116f" strokeLinecap="round" strokeWidth=".568472" d="M14.3982 20.4446c-.6659.7213-2.0537.7073-2.6827.0176" />

            {/* Eyebrows */}
            <path
              stroke="#33116f"
              strokeLinecap="round"
              strokeWidth=".568472"
              d="M6.64275 14.8685c.80599-.6264 1.83415-.7829 2.78935-.4245M19.4986 14.938c-.806-.6264-1.8341-.7829-2.7893-.4245"
            />

            {/* Hood / hair silhouette */}
            <path
              fill={`url(#${id('h')})`}
              d="m23.2012 6.74477-.1478-.16391-.6955-.69164-.2482-.2217c-.8944-.79871-1.9091-1.43918-2.9996-1.95649-1.8201-.86313-3.8041-1.31602-5.8146-1.35391h-.6253c-2.2189.05116-4.40753.59973-6.36781 1.65235-.97208.52205-1.86174 1.16253-2.67276 1.89775l-.17717.16107c-.13454.12222-.2615.25581-.38467.38845l-.27287.2975C1.12551 8.57241-.00006104 11.213-.00006104 13.7085v8.7744c0 .9891.40930004 1.9205 1.03746104 2.6529.45099.5258 1.00904.8982 1.63341 1.1407-.10327-.3761-.23781-1.1455-.03316-2.1971 0 0 .2577 1.6883.92661 2.451.50025.1052.98819.2018 1.49223.2701-.05684-.1659-.19517-.6746-.05021-1.4193 0 0 .15728 1.0298.56468 1.4837l1.19948.1393.35245.0284.28802.0237.32877.0265.31266.0246.35908.0266.33351.0246.38466.0265.43678.0265.44623.0256.4292.019.3847.0161.8025.018 2.4889-.0019 1.0754-.0284c1.7367-.0465 3.4468-.18 5.1702-.3781.018-.0019.036-.0047.054-.0066.416-.4481.5761-1.4951.5761-1.4951.1478.7618 0 1.2762-.054 1.4288.505-.0701 1.0024-.1592 1.4989-.2843.666-.7646.9228-2.4463.9228-2.4463.1971 1.0147.0786 1.7661-.0228 2.1555.4605-.1668.8651-.4292 1.2346-.7504l.4055-.4027c.5865-.7096 1.0213-1.6116 1.0213-2.5714v-8.8539c0-2.4928-1.1445-5.08215-2.7987-6.91168zm.0265 10.36233c-.2236 2.2161-1.5472 3.4421-3.2204 4.67l-.5798.3988c-3.7823 2.1792-8.9459 2.2038-12.75655.0749l-.57889-.3525c-1.54719-1.0914-3.02048-2.254-3.31987-4.7628-.03222-.6755.00663-1.2687.2757-1.786.0578-.1013.81481-1.5415 2.85942-2.7343.20938-.108.41688-.2198.62342-.3335h-.00095c1.87217-1.0403 3.52172-2.2711 4.94572-3.60602.0767-.07106.1355-.12696.252-.19707-.271 1.14926-1.3624 2.34779-2.19713 3.13889-.25582.2369-.79586.6793-1.07063.9048 3.07256.3468 6.17176-1.0422 8.32906-3.18056.0161-.01516.0796-.0199.0881-.00379l.0284.06064c.9172 1.73191 2.6207 3.00341 4.3479 3.86841 1.0791.487 2.0958 2.1962 1.9754 3.8391z"
            />
            {/* Hair rainbow band */}
            <path
              fill={`url(#${id('i')})`}
              d="M13.1188 7.82106c-7.39769 0-10.79778 4.37884-10.93962 7.18504-.67841-3.4846 2.21077-9.80618 10.93962-9.80618 8.7288 0 11.2012 6.10568 10.7695 9.83698-.274-3.1145-3.3718-7.21584-10.7695-7.21584"
            />
            {/* Neck / collar */}
            <path
              fill={`url(#${id('j')})`}
              d="M17.442 27.1816c-2.9062.1938-5.821.1979-8.72752.0117l.70703-3.8799c1.11879.2953 2.33879.46 3.64449.46 1.2725 0 2.508-.1719 3.6661-.4883z"
            />
            <path
              fill={`url(#${id('k')})`}
              d="M16.9014 23.2393c1.1226.5548 1.9364 1.5238 2.456 3.4628l.084.3165c-.8271.0801-1.6556.1441-2.4844.1933l-1.0009-3.7383c.3203-.0674.6357-.1454.9453-.2343"
            />
            <path
              fill={`url(#${id('l')})`}
              d="M9.1535 23.2402c.29823.0856.60405.1606.917.2266l-1.00391 3.749c-.79529-.0467-1.59-.1084-2.38379-.1836l-.06152-.0615.04297-.253c.52466-1.9578 1.34957-2.9254 2.48925-3.4775"
            />
            <path
              fill="#5217b4"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M19.4427 22.2024c.2353.1374.3809.4314.2901.8945-.3475 1.7693-1.2814 1.6514-2.0225 1.3906-1.7058-.6001-3.5384.0633-4.7705.5372-.2146.0816-.6629-.7903.1582-1.2325.097-.0069.1988-.0174.3057-.0263 2.209-.0522 4.296-.6121 6.039-1.5635"
            />
            <path
              fill="#33116f"
              d="M19.1259 22.3672c.2447.1461.2836.3213.1015.4775-.4423.5055-2.3108.5479-3.6015.6953.0076-.0023.0149-.0054.0224-.0078 1.2449-.2324 2.4193-.6282 3.4776-1.165"
            />
            <path
              fill="#5217b4"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.63597 22.2041c1.6949.9414 3.78173 1.5227 6.16213 1.5635.0561.0083.1128.0169.1699.0254.7621.3476.1237 1.3147-.0908 1.2324-1.2321-.4739-2.8157-1.1372-4.5215-.5371-.74112.2608-1.67495.3786-2.02246-1.3906-.08552-.4356.0725-.7374.30273-.8936"
            />
            <path
              fill="#33116f"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.97527 22.3838c1.00281.5132 2.13252.9034 3.37113 1.1377-1.27993-.1318-3.0232-.1961-3.44438-.6768-.17526-.1502-.14658-.3182.07325-.4609"
            />
          </g>

          {/* Horn / sparkle (outside the clip so it can extend past the top) */}
          <path
            fill={`url(#${id('m')})`}
            d="M12.6678.328697 10.6933 5.87352c-.1156.32463.0698.67895.4024.76909 1.3392.36296 2.7504.36611 4.0912.00912l.032-.00854c.3393-.09033.5291-.45112.4113-.78185L13.6601.328697c-.1855-.438263-.8068-.438263-.9923 0"
          />
          <mask id={id('n')} width="6" height="7" x="10" y="0" maskUnits="userSpaceOnUse" style={{ maskType: 'alpha' }}>
            <path
              fill="#f97df3"
              d="M12.6678.329002 10.7042 5.84346c-.1215.34101.0732.7132.4226.80789 1.3188.35744 2.7086.36054 4.0289.00898l.0315-.00838c.3563-.09488.5557-.47386.432-.82125L13.6601.329002c-.1855-.438262-.8068-.438262-.9923 0"
            />
          </mask>
          <g stroke="#8c4ff7" strokeWidth=".462802" mask={`url(#${id('n')})`}>
            <path d="m10.3431 6.01636 5.8175-4.30414M11.9847 7.21979l5.8175-4.30414M10.4588 3.54246 15.559-.185211" />
          </g>

          <defs>
            <linearGradient id={id('c')} x1="13.0269" x2="13.0435" y1="24.5502" y2="8.91312" gradientUnits="userSpaceOnUse">
              <stop stopColor="#fff8a8" />
              <stop offset=".490385" stopColor="#ffd6a3" />
              <stop offset="1" stopColor="#fba7f7" />
            </linearGradient>
            <linearGradient id={id('h')} x1="12.9999" x2="12.9999" y1="2.35712" y2="27.2903" gradientUnits="userSpaceOnUse">
              <stop stopColor="#762bfa" />
              <stop offset=".5" stopColor="#8c4ff7" />
              <stop offset="1" stopColor="#b48df8" />
            </linearGradient>
            <linearGradient id={id('i')} x1="2.39189" x2="23.9199" y1="10.1229" y2="9.74156" gradientUnits="userSpaceOnUse">
              <stop stopColor="#f97df3" />
              <stop offset=".33" stopColor="#ffbf70" />
              <stop offset=".67" stopColor="#ffe928" />
              <stop offset="1" stopColor="#6ee66c" />
            </linearGradient>
            <linearGradient id={id('j')} x1="13.1033" x2="13.0782" y1="25.0277" y2="27.33" gradientUnits="userSpaceOnUse">
              <stop stopColor="#8c4ff7" />
              <stop offset="1" stopColor="#762bfa" />
            </linearGradient>
            <linearGradient id={id('k')} x1="18.4418" x2="17.2646" y1="26.3616" y2="27.2135" gradientUnits="userSpaceOnUse">
              <stop stopColor="#5217b4" />
              <stop offset="1" stopColor="#33116f" />
            </linearGradient>
            <linearGradient id={id('l')} x1="7.60681" x2="8.88948" y1="26.3785" y2="27.2013" gradientUnits="userSpaceOnUse">
              <stop stopColor="#5217b4" />
              <stop offset="1" stopColor="#33116f" />
            </linearGradient>
            <linearGradient id={id('m')} x1="13.1639" x2="13.1639" y1="0" y2="7.19671" gradientUnits="userSpaceOnUse">
              <stop stopColor="#762bfa" />
              <stop offset="1" stopColor="#33116f" />
            </linearGradient>
            <radialGradient
              id={id('d')}
              cx="0"
              cy="0"
              r="1"
              gradientTransform="matrix(-1.57268 2.24339 2.26581 1.58841 18.5318 16.5631)"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#c2a0ff" />
              <stop offset="1" stopColor="#33116f" />
            </radialGradient>
            <radialGradient
              id={id('e')}
              cx="0"
              cy="0"
              r="1"
              gradientTransform="matrix(-1.16783 1.68254 1.68254 1.19131 17.7605 16.5368)"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#c2a0ff" />
              <stop offset="1" stopColor="#33116f" />
            </radialGradient>
            <radialGradient
              id={id('f')}
              cx="0"
              cy="0"
              r="1"
              gradientTransform="matrix(-1.57268 2.24339 2.26581 1.58841 9.71849 16.5415)"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#c2a0ff" />
              <stop offset="1" stopColor="#33116f" />
            </radialGradient>
            <radialGradient
              id={id('g')}
              cx="0"
              cy="0"
              r="1"
              gradientTransform="matrix(-1.16783 1.68254 1.68254 1.19131 8.95546 16.4674)"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#c2a0ff" />
              <stop offset="1" stopColor="#33116f" />
            </radialGradient>
            <clipPath id={id('a')}>
              <path fill="#fff" d="M0 2.35718h26v24.9341H0z" />
            </clipPath>
            <clipPath id={id('rightWhiteClip')}>
              <path d={RIGHT_EYE_WHITE} />
            </clipPath>
            <clipPath id={id('leftWhiteClip')}>
              <path d={LEFT_EYE_WHITE} />
            </clipPath>
            <filter
              id={id('b')}
              width="21.8498"
              height="18.7327"
              x="2.10199"
              y="5.81755"
              colorInterpolationFilters="sRGB"
              filterUnits="userSpaceOnUse"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
              <feOffset dy="-1.10933" />
              <feGaussianBlur stdDeviation=".827971" />
              <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
              <feColorMatrix values="0 0 0 0 0.975661 0 0 0 0 0.490762 0 0 0 0 0.951416 0 0 0 1 0" />
              <feBlend in2="shape" result="effect1_innerShadow_4970_69287" />
            </filter>
          </defs>
        </svg>
      </div>
    </div>
  );
}
